package dev.lingvo.site.pages.app.settings.agents

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.AgentAutonomy
import dev.lingvo.shared.BillingOverviewDto
import dev.lingvo.shared.CreateAgentConnectionRequest
import dev.lingvo.shared.TokenScope
import dev.lingvo.site.api.Async
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LCheckbox
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.state.ProjectDirectory
import dev.lingvo.site.state.WorkspaceActionClaim
import dev.lingvo.site.state.WorkspaceRequestContext
import kotlinx.coroutines.CoroutineScope
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
internal fun AgentConnectionForm(
    state: NewAgentPageState,
    pageClaim: WorkspaceActionClaim,
    scope: CoroutineScope,
    billingState: Async<BillingOverviewDto?>,
    requestedProjectSlugs: List<String>,
    directoryVerified: Boolean,
    unknownProjectSlugs: List<String>,
    typedAllowlistBlocked: Boolean,
    onCancel: () -> Unit,
) = with(state) {
    // Only ever on screen because a create attempt just failed, so it is announced rather than
    // left for the reader to find on their way back up the form.
    message?.let {
        LAlert(
            t("app.settings.agents.agent_connection_form.that_didnt_work", "That didn't work"),
            it,
            variant = LAlertVariant.WARNING,
            live = true,
        )
    }

    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            AgentFormStep("1", t("app.settings.agents.agent_connection_form.what_is_it", "What is it?"))
            LTextField(
                name,
                { name = it },
                Modifier.fillMaxWidth(),
                label = t("app.settings.agents.agent_connection_form.name", "Name"),
                placeholder = t("app.settings.agents.agent_connection_form.release_bot", "Release bot"),
            )
            AgentFormMuted(t("app.settings.agents.agent_connection_form.used_in_the_audit_log_and", "Used in the audit log and on every refusal, so make it the thing you'd search for."))
        }
    }

    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            AgentFormStep("2", t("app.settings.agents.agent_connection_form.what_may_it_do", "What may it do?"))
            // Presets rather than a bare checklist: each names the consequence, while the exact
            // scopes stay visible underneath so nobody is choosing blind.
            LSelect(
                value = preset.name,
                options = AgentPreset.entries.map { LSelectOption(it.name, it.label()) },
                onValueChange = { raw ->
                    AgentPreset.entries.firstOrNull { it.name == raw }?.let {
                        preset = it
                        scopes = it.scopes
                        autonomy = it.autonomy
                    }
                },
                label = t("app.settings.agents.agent_connection_form.preset", "Preset"),
            )
            AgentFormMuted(preset.consequence())
            AgentFormMuted(t("app.settings.agents.agent_connection_form.or_choose_scope_by_scope_the", "Or choose scope by scope — the consequence is stated, not the label."))
            TokenScope.entries.forEach { option ->
                LCheckbox(
                    checked = option in scopes,
                    onCheckedChange = { on -> scopes = if (on) scopes + option else scopes - option },
                    label = tf("app.settings.agents.agent_connection_form.option_and_consequence", "{option} — {consequence}", "option" to option.id, "consequence" to option.consequence),
                    enabled = !busy,
                )
            }
            // One sentence, one key: half of it wrapped and half hard-coded rendered the two
            // clauses in two languages, and no amount of translating could fix the second.
            AgentFormMuted(t("app.settings.agents.agent_connection_form.it_borrows_your_authority_as_it", "It borrows your authority as it is at the time of each call, not as it is now. If your role narrows, the agent narrows with it."))
        }
    }

    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            AgentFormStep("3", t("app.settings.agents.agent_connection_form.which_projects", "Which projects?"))
            LTextField(
                projects,
                { projects = it },
                Modifier.fillMaxWidth(),
                label = t("app.settings.agents.agent_connection_form.project_slugs_comma_separated", "Project slugs, comma separated"),
                placeholder = t("app.settings.agents.agent_connection_form.leave_empty_for_every_project", "leave empty for every project"),
            )
            if (directoryVerified) {
                val slugs = ProjectDirectory.projects.map { it.slug }
                if (slugs.isNotEmpty()) {
                    Span(Modifier.monoFont().fontSize(TypeScale.Small).toAttrs()) {
                        Text(slugs.joinToString(", "))
                    }
                }
            }
            AgentFormMuted(agentProjectScopeNote(everyProject = projects.isBlank()))
            if (requestedProjectSlugs.isNotEmpty() && !directoryVerified) {
                val directoryError = ProjectDirectory.error
                LAlert(
                    t("app.settings.agents.agent_connection_form.project_access_is_not_verified_yet", "Project access is not verified yet"),
                    directoryError ?: agentDirectoryCheckingNote(),
                    variant = if (directoryError == null) LAlertVariant.INFO else LAlertVariant.WARNING,
                    action = directoryError?.let {
                        {
                            LButton(
                                t("app.settings.agents.agent_connection_form.retry", "Retry"),
                                {
                                    val retryClaim = pageClaim
                                        .takeIf { it.remainsAuthorized(WorkspaceRequestContext.current()) }
                                        ?: return@LButton
                                    WorkspaceRequestContext.current()
                                        ?.takeIf(retryClaim::remainsAuthorized)
                                        ?.let { ProjectDirectory.ensure(scope, it, force = true) }
                                },
                                variant = LButtonVariant.GHOST,
                            )
                        }
                    },
                )
            } else if (unknownProjectSlugs.isNotEmpty()) {
                LAlert(
                    t("app.settings.agents.agent_connection_form.choose_projects_from_this_workspace", "Choose projects from this workspace"),
                    tf(
                        "app.settings.agents.agent_connection_form.unknown_here_slugs",
                        "Unknown here: {slugs}.",
                        "slugs" to unknownProjectSlugs.joinToString(", "),
                    ),
                    variant = LAlertVariant.WARNING,
                )
            }
        }
    }

    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            AgentFormStep("4", t("app.settings.agents.agent_connection_form.what_do_its_writes_do", "What do its writes do?"))
            LSelect(
                value = autonomy.name,
                options = AgentAutonomy.entries.map { LSelectOption(it.name, it.description) },
                onValueChange = { raw ->
                    AgentAutonomy.entries.firstOrNull { it.name == raw }?.let { autonomy = it }
                },
                label = t("app.settings.agents.agent_connection_form.autonomy", "Autonomy"),
            )
            AgentFormMuted(t("app.settings.agents.agent_connection_form.no_setting_lets_an_agent_overwrite", "No setting lets an agent overwrite an approved translation."))
            if (autonomy != AgentAutonomy.PROPOSE) {
                LAlert(
                    t("app.settings.agents.agent_connection_form.its_writes_will_land_directly", "Its writes will land directly"),
                    t("app.settings.agents.agent_connection_form.proposals_are_the_reviewable_option_everything_2", "Proposals are the reviewable option: everything queues on the project's " +
                        "proposals page and a person accepts it. You can change this later."),
                    variant = LAlertVariant.INFO,
                )
            }
        }
    }

    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            AgentFormStep("5", t("app.settings.agents.agent_connection_form.what_may_it_spend", "What may it spend?"))
            if (TokenScope.MT_SPEND !in scopes) {
                AgentFormMuted(t("app.settings.agents.agent_connection_form.nothing_it_has_no_spending_scope", "Nothing. It has no spending scope, so the budgets stay at zero and it cannot start a paid translation at all."))
            } else {
                AgentFormMuted(t("app.settings.agents.agent_connection_form.all_three_are_required_and_all", "All three are required and all three start at zero, so every agent that can spend money is one where somebody typed a number."))
                Row(Modifier.fillMaxWidth().gap(Space.SM)) {
                    LTextField(
                        perCall,
                        { perCall = it },
                        Modifier.flexGrow(1),
                        label = t("app.settings.agents.agent_connection_form.max_credits_per_call", "Max credits per call"),
                    )
                    LTextField(
                        daily,
                        { daily = it },
                        Modifier.flexGrow(1),
                        label = t("app.settings.agents.agent_connection_form.credits_per_day", "Credits per day"),
                    )
                    LTextField(
                        monthly,
                        { monthly = it },
                        Modifier.flexGrow(1),
                        label = t("app.settings.agents.agent_connection_form.credits_per_month", "Credits per month"),
                    )
                }
                when (billingState) {
                    is Async.Success -> billingState.value?.let { billing ->
                        AgentFormMuted(agentCreditsNote(billing.creditsRemaining))
                    }
                    // A failed billing read used to render as no line at all, so the one card where
                    // somebody types a number against a balance said nothing about the balance and
                    // nothing about not knowing it.
                    is Async.Failure ->
                        AgentFormMuted(t("app.settings.agents.agent_connection_form.couldnt_read_this_workspaces_remaining_credits", "Couldn't read this workspace's remaining credits, so these numbers are not being checked against a balance."))
                    Async.Loading -> Unit
                }
                AgentFormMuted(t("app.settings.agents.agent_connection_form.per_call_is_a_hard_ceiling", "Per call is a hard ceiling: a call above it is refused outright. Going over the day or month asks an admin instead, on the approvals page."))
            }
        }
    }

    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            AgentFormStep("6", t("app.settings.agents.agent_connection_form.when_does_it_stop_working", "When does it stop working?"))
            LTextField(
                expiresInDays,
                { expiresInDays = it },
                Modifier.fillMaxWidth(),
                label = t("app.settings.agents.agent_connection_form.days_until_it_expires", "Days until it expires"),
                placeholder = "30",
            )
            AgentFormMuted(t("app.settings.agents.agent_connection_form.thirty_by_default_ninety_at_most", "Thirty by default, ninety at most, and there is no \"never\": a credential with no end date outlives the reason it was created. Renewing it is one click by somebody who is still here, which is the check being bought."))
        }
    }

    Row(Modifier.gap(Space.SM)) {
        CreateAgentConnectionAction(
            pageClaim = pageClaim,
            request = CreateAgentConnectionRequest(
                name = name.trim(),
                scopes = scopes.sortedBy { it.ordinal },
                projectSlugs = requestedProjectSlugs,
                autonomy = autonomy,
                creditBudgetMonthly = monthly.toIntOrNull() ?: 0,
                creditBudgetDaily = daily.toIntOrNull() ?: 0,
                maxCreditsPerCall = perCall.toIntOrNull() ?: 0,
                expiresInDays = expiresInDays.toIntOrNull() ?: 30,
            ),
            enabled = name.isNotBlank() && scopes.isNotEmpty() && !typedAllowlistBlocked && !busy,
            busy = busy,
            setBusy = { busy = it },
            onCreated = { result ->
                created = result
                message = null
            },
            onFailure = { error ->
                message = error.message?.takeIf(String::isNotBlank)
                    ?: agentConnectionRefused()
            },
        )
        LButton(
            t("app.settings.agents.agent_connection_form.cancel", "Cancel"),
            onCancel,
            variant = LButtonVariant.GHOST,
        )
    }
}

/** The three shapes an agent connection actually takes. */
internal enum class AgentPreset(
    val scopes: Set<TokenScope>,
    val autonomy: AgentAutonomy,
) {
    READ_ONLY(
        setOf(TokenScope.STRINGS_READ, TokenScope.QA_RUN, TokenScope.GLOSSARY_READ),
        AgentAutonomy.PROPOSE,
    ),
    PROPOSE(
        setOf(TokenScope.STRINGS_READ, TokenScope.QA_RUN, TokenScope.GLOSSARY_READ, TokenScope.STRINGS_WRITE),
        AgentAutonomy.PROPOSE,
    ),
    SPEND(
        setOf(
            TokenScope.STRINGS_READ,
            TokenScope.QA_RUN,
            TokenScope.GLOSSARY_READ,
            TokenScope.STRINGS_WRITE,
            TokenScope.MT_SPEND,
        ),
        AgentAutonomy.PROPOSE,
    ),
}

/**
 * The preset names, looked up rather than held as enum fields.
 *
 * An enum constructor argument is a class-init value: it is computed once, before anybody has a
 * locale, so a `t(...)` cannot live there. These three names and the three sentences under them
 * decide whether a connection may spend money, and they were the only strings on this form that
 * stayed English in every locale. Same shape as `ProjectKind.displayLabel()`.
 */
internal fun AgentPreset.label(): String = when (this) {
    AgentPreset.READ_ONLY -> tr("app.settings.agents.agent_connection_form.preset_read_only", "Read only")
    AgentPreset.PROPOSE -> tr("app.settings.agents.agent_connection_form.preset_propose", "Propose")
    AgentPreset.SPEND -> tr("app.settings.agents.agent_connection_form.preset_translate_with_credits", "Translate with credits")
}

/** What each preset lets the connection do, in the reader's language. */
internal fun AgentPreset.consequence(): String = when (this) {
    AgentPreset.READ_ONLY -> tr(
        "app.settings.agents.agent_connection_form.it_can_look_at_strings_run",
        "It can look at strings, run QA and read the glossary. It cannot change anything or spend anything.",
    )
    AgentPreset.PROPOSE -> tr(
        "app.settings.agents.agent_connection_form.it_can_suggest_translations_and_leave",
        "It can suggest translations and leave comments. Everything it writes waits on the proposals page.",
    )
    AgentPreset.SPEND -> tr(
        "app.settings.agents.agent_connection_form.it_can_start_machine_translation_which",
        "It can start machine translation, which costs money — so all three budgets have to be set.",
    )
}

@Composable
private fun AgentFormStep(number: String, title: String) {
    Row(Modifier.gap(Space.SM)) {
        Span(
            Modifier.monoFont().color(ColorMode.current.toSitePalette().textSecondary).toAttrs(),
        ) { Text(number) }
        LSectionTitle(title)
    }
}

@Composable
private fun AgentFormMuted(text: String) {
    P(
        Modifier
            .color(ColorMode.current.toSitePalette().textSecondary)
            .fontSize(TypeScale.Small)
            .margin(0.px)
            .toAttrs(),
    ) {
        Text(text)
    }
}

/**
 * What the project allowlist means, in whichever of its two states it is in.
 *
 * `AgentFormMuted` renders a visible paragraph and is not on the extractor's positional list, so
 * six prose lines on this form — these two, the directory notice below, and the credit-cost
 * sentence — were the only strings on it that never reached the catalogue. A German-locale owner
 * met a form whose labels and buttons were German and whose explanations were not.
 */
internal fun agentProjectScopeNote(everyProject: Boolean): String = if (everyProject) {
    tr(
        "app.settings.agents.agent_connection_form.empty_means_every_project_in_this",
        "Empty means every project in this workspace, including ones created later.",
    )
} else {
    tr(
        "app.settings.agents.agent_connection_form.anything_else_is_refused_with_a",
        "Anything else is refused with a message naming these projects — never the ones it cannot reach.",
    )
}

/** Said while the typed project names are still being checked against the workspace. */
internal fun agentDirectoryCheckingNote(): String = tr(
    "app.settings.agents.agent_connection_form.lingvo_is_checking_these_project_names",
    "Lingvo is checking these project names against this workspace.",
)

/**
 * The balance a budget is being typed against, and what a credit buys.
 *
 * Three literals joined with `+` before this — banned as copy by the plan, and the join fixed the
 * number's position in a sentence no locale could reorder. One message, one named placeholder.
 */
internal fun agentCreditsNote(creditsRemaining: Int): String = trf(
    "app.settings.agents.agent_connection_form.this_workspace_has_credits_credits_left",
    "This workspace has {credits} credits left this month. One credit is one string of up to 1,000 characters into one language; Claude and OpenAI cost three.",
    "credits" to creditsRemaining,
)

/** What this form's one failure path says. */
internal fun agentConnectionRefused(): String = tr(
    "app.settings.agents.agent_connection_form.couldnt_create_this_agent_connection_retry",
    "Couldn't create this agent connection. Retry?",
)
